<template>
  <auth-page>
    <h2 class="brand-text">Sign Up</h2>
    <b-form v-if="!success" @submit="onSubmit">
      <p>
        Enter your info to sign up for an account.
        We'll send you instructions to complete your registration.
      </p>
      <b-form-group>
        <label class="sr-only" for="email-input">Email</label>
        <input
          v-validate="'required|email'"
          id="email-input"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('email') || apiErrors.email.length }"
          class="form-control"
          type="text"
          name="email"
          placeholder="Email"
          @input="setEmail"/>
        <validation-errors :validation-errors="[errors.first('email')]"/>
        <span v-if="!errors.has('email')">
          <validation-errors :validation-errors="apiErrors.email"/>
        </span>
      </b-form-group>
      <b-form-group>
        <label class="sr-only" for="name-input">Full Name</label>
        <input
          v-validate="'required|alpha_spaces'"
          id="name-input"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('name') || apiErrors.name.length}"
          class="form-control"
          type="text"
          name="name"
          placeholder="Full Name"
          @input="setName"/>
        <validation-errors :validation-errors="[errors.first('name')]"/>
        <span v-if="!errors.has('name')">
          <validation-errors :validation-errors="apiErrors.name"/>
        </span>
      </b-form-group>
      <b-form-group>
        <label class="sr-only" for="password-input">Password</label>
        <input
          v-validate="'required|min:8|lowercase|uppercase|numberOrSpecial'"
          id="password-input"
          ref="password"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('password') || apiErrors.password.length}"
          class="form-control"
          type="password"
          name="password"
          placeholder="Password"
          @input="setPassword"/>
        <validation-errors :validation-errors="[errors.first('password')]"/>
        <span v-if="!errors.has('password')">
          <validation-errors :validation-errors="apiErrors.password"/>
        </span>
      </b-form-group>
      <b-form-group>
        <label class="sr-only" for="password-confirm">Confirm Password</label>
        <input
          v-validate ="{ rules: { required: confirmationIsRequired, confirmed: [ 'password' ] }}"          
          id="password-confirm"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('password-confirm') }"
          class="form-control"
          type="password"
          name="password-confirm"
          data-vv-as="password"
          placeholder="Confirm Password"/>
        <validation-errors :validation-errors="[errors.first('password-confirm')]"/>
        <span v-if="!errors.has('password')">
          <validation-errors :validation-errors="apiErrors.password"/>
        </span>
      </b-form-group>
      <b-tooltip target="password-input" triggers="focus" placement="left">
        <password-rules/>
      </b-tooltip>
      <b-form-group>
        <vue-recaptcha ref="recaptcha" :sitekey="sitekey" class="recaptcha" @verify="onVerify"/>
        <validation-errors :validation-errors="apiErrors.recaptcha_token"/>
      </b-form-group>
      <b-form-group>
        <b-button :disabled="disabled" type="submit" block variant="light">
          Sign Up
          <span v-if="saving"><i class="fas fa-spinner fa-spin"></i></span>
        </b-button>
      </b-form-group>
      <form-fail :form-errors="apiErrors.other"/>
    </b-form>
    <form-success :success="success">
      <strong>Welcome to IIP!</strong>
      You may
      <router-link :to="{name: 'login', query: {email: fields.email}}" style="text-decoration:underline">login</router-link>
      to your account right away.
      <br>
      <br>
      We've also sent you an email to <i>{{ fields.email }}</i> to confirm your address.
      <br>
    </form-success>
    <router-link :to="{name: 'login'}" class="link-light">
      <i class="fas fa-hand-point-left"></i>
      &nbsp;back to login
    </router-link>
  </auth-page>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import authPage from '../../templates/auth-page'
import config from '../../../config'
import formFail from '../../common/form/form-fail'
import passwordRules from '../../common/form/password-rules'
import formSuccess from '../../common/form/form-success'
import validationErrors from '../../common/form/validation-errors'
import vueRecaptcha from 'vue-recaptcha'

export default {
  components: {
    authPage,
    formFail,
    formSuccess,
    passwordRules,
    validationErrors,
    vueRecaptcha
  },
  data () {
    return {
      sitekey: config.recaptchaKey,
      captchaValid: false
    }
  },
  computed: {
    ...mapState('register/forms/signUp/', [
      'apiErrors',
      'success',
      'fields',
      'saving'
    ]),
    disabled () {
      return this.errors.any() || this.captchaValid === false || this.saving
    },
    confirmationIsRequired () {
      if (this.fields.password === null || this.fields.password === '') {
        return false
      }

      return true
    }
  },
  methods: {
    ...mapActions({
      signUp: 'register/signUp'
    }),
    onSubmit (e) {
      e.preventDefault()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.signUp()
        }
      })
    },
    setEmail (e) {
      this.$store.commit('register/forms/signUp/setField', {
        field: 'email',
        value: e.target.value
      })
    },
    setName (e) {
      this.$store.commit('register/forms/signUp/setField', {
        field: 'name',
        value: e.target.value
      })
    },
    setPassword (e) {
      this.$store.commit('register/forms/signUp/setField', {
        field: 'password',
        value: e.target.value
      })
    },
    onVerify (token) {
      this.$store.commit('register/forms/signUp/setField', {
        field: 'recaptcha_token',
        value: token
      })
      this.captchaValid = true
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/auth/sign-up.vue